import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { activateLicense } from '../utils/licenseHelper';
import { getApiUrl, API_ENDPOINTS } from '../config/apiConfig';

const ActivationContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    background: var(--bg-color);
    padding: 20px;
`;

const ActivationForm = styled.div`
    background: var(--glass-bg);
    border-radius: 12px;
    padding: 30px;
    border: 1px solid var(--border-color);
    max-width: 500px;
    width: 100%;
`;

const LicenseActivation = () => {
    const [licenseKey, setLicenseKey] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [isCheckingAuth, setIsCheckingAuth] = useState(true);
    const navigate = useNavigate();

    // Sayfa yüklendiğinde önce yetki kontrolü, sonra lisans kontrolü
    useEffect(() => {
        checkAuthAndLicense();
    }, []);

    const checkAuthAndLicense = async () => {
        // Önce yetki kontrolü
        const hasAccess = await checkAdminRole();
        if (!hasAccess) {
            return; // Yönlendirme zaten yapıldı
        }
        
        // Yetki varsa lisans kontrolü yap
        checkExistingLicense();
    };

    const checkAdminRole = async () => {
        try {
            const response = await fetch(getApiUrl(API_ENDPOINTS.CHECK_AUTH), {
                credentials: 'include'
            });
            
            if (!response.ok) {
                // Hata durumunda direkt ana sayfaya yönlendir, mesaj gösterme
                navigate('/', { replace: true });
                return false;
            }
            
            const data = await response.json();
            
            // Auth başarısız veya kullanıcı yok - direkt ana sayfaya yönlendir
            if (!data.success || !data.user) {
                navigate('/', { replace: true });
                return false;
            }
            
            // ROLE KONTROLÜ - admin veya moderator
            if (data.user.role && (data.user.role === 'admin' || data.user.role === 'moderator')) {
                setIsCheckingAuth(false);
                return true;
            } else {
                // Admin/moderator değil - ana sayfaya yönlendir
                navigate('/', { replace: true });
                return false;
            }
        } catch (error) {
            // Hata durumunda - ana sayfaya yönlendir
            navigate('/', { replace: true });
            return false;
        }
    };

    const checkExistingLicense = async () => {
        const savedLicense = localStorage.getItem('site_license');
        if (savedLicense) {
            try {
                const licenseData = JSON.parse(savedLicense);
                const currentDomain = window.location.hostname;
                
                // Domain kontrolü - eşleşiyorsa direkt admin'e yönlendir
                if (licenseData.domain === currentDomain && licenseData.key) {
                    // Lisans var ve domain eşleşiyor - direkt geç
                    // LicenseCheck component'i arka planda kontrol edecek
                    navigate('/admin');
                } else {
                    // Domain eşleşmiyor veya key yok - temizle
                    localStorage.removeItem('site_license');
                }
            } catch (error) {
                localStorage.removeItem('site_license');
            }
        }
    };

    const handleActivate = async () => {
        if (!licenseKey.trim()) {
            setMessage('Lütfen license key girin');
            return;
        }

        setIsLoading(true);
        setMessage('');

        try {
            const domain = window.location.hostname;

            // Uzak lisans sunucusuna istek gönder
            const result = await activateLicense(licenseKey.trim(), domain);

            if (result.success) {
                // License bilgisini kaydet
                const licenseInfo = {
                    key: licenseKey.trim(),
                    domain: domain,
                    license: result.license,
                    activated_at: new Date().toISOString(),
                    last_validation: new Date().toISOString()
                };
                
                localStorage.setItem('site_license', JSON.stringify(licenseInfo));

                setMessage('✅ License başarıyla aktifleştirildi!');
                setTimeout(() => {
                    navigate('/admin');
                }, 2000);
            } else {
                setMessage(`❌ ${result.message}`);
            }
        } catch (error) {
            setMessage('❌ License aktivasyonu sırasında hata oluştu: ' + error.message);
        } finally {
            setIsLoading(false);
        }
    };

    // Yetki kontrolü yapılıyorsa loading göster
    if (isCheckingAuth) {
        return (
            <ActivationContainer>
                <ActivationForm>
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        Yetki kontrol ediliyor...
                    </div>
                </ActivationForm>
            </ActivationContainer>
        );
    }

    return (
        <ActivationContainer>
            <ActivationForm>
                <h1>🚀 License Aktivasyonu</h1>
                <p style={{ color: 'var(--secondary-color)', marginBottom: '20px' }}>
                    Sitenizi aktifleştirmek için license keyinizi girin
                </p>

                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                        License Key
                    </label>
                    <input
                        type="text"
                        value={licenseKey}
                        onChange={(e) => setLicenseKey(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '12px',
                            background: 'rgba(0,0,0,0.3)',
                            border: '1px solid var(--border-color)',
                            borderRadius: '8px',
                            color: 'white',
                            fontSize: '1rem'
                        }}
                    />
                </div>

                <button
                    onClick={handleActivate}
                    disabled={isLoading}
                    style={{
                        width: '100%',
                        padding: '12px',
                        background: 'var(--gradient-pink)',
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '1rem',
                        fontWeight: '600',
                        cursor: isLoading ? 'not-allowed' : 'pointer',
                        opacity: isLoading ? 0.7 : 1
                    }}
                >
                    {isLoading ? 'Aktifleştiriliyor...' : 'License Aktifleştir'}
                </button>

                {message && (
                    <div style={{
                        marginTop: '15px',
                        padding: '10px',
                        background: message.includes('✅') ? 'rgba(46, 213, 115, 0.2)' : 'rgba(255, 71, 87, 0.2)',
                        border: `1px solid ${message.includes('✅') ? '#2ed573' : '#ff4757'}`,
                        borderRadius: '8px',
                        color: message.includes('✅') ? '#2ed573' : '#ff4757'
                    }}>
                        {message}
                    </div>
                )}

                <div style={{ marginTop: '20px', fontSize: '0.9rem', color: 'var(--secondary-color)' }}>
                    <p><strong>Domain:</strong> {window.location.hostname}</p>
                    <p>License key almak için: <strong>info@cineglasstheme.com</strong></p>
                </div>

                {/* Debug Info */}
                <div style={{ 
                    marginTop: '20px', 
                    padding: '10px', 
                    background: 'rgba(0,0,0,0.3)', 
                    borderRadius: '8px',
                    fontSize: '0.8rem',
                    color: '#b2bec3'
                }}>
                    <strong>Debug Info:</strong>
                    <div>Domain: {window.location.hostname}</div>
                    <div>License Key: {licenseKey || 'Boş'}</div>
                </div>
            </ActivationForm>
        </ActivationContainer>
    );
};

export default LicenseActivation;
